import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { fadeUp, viewport, stagger } from "@/lib/motion";
import { HeartHandshake, Pill, ScanLine, Activity, Sparkles, Loader2, Check, X } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function RecoveryProgramInterest() {
  const [count, setCount] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/program/interest`);
        setCount(r.data.count);
      } catch (e) { /* silent */ }
    })();
    // remember if this browser already reserved a seat
    if (localStorage.getItem("sa_program_interest_fp")) setJoined(true);
  }, []);

  const submit = async () => {
    if (submitting) return;
    if (!phone.trim() || phone.trim().length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }
    setSubmitting(true);
    try {
      const fp = localStorage.getItem("sa_fp") || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("sa_fp", fp);
      const r = await axios.post(`${API}/program/interest`, {
        fingerprint: fp,
        name: name.trim(),
        phone: phone.trim(),
      });
      setCount(r.data.count);
      localStorage.setItem("sa_program_interest_fp", "1");
      setJoined(true);
      setModalOpen(false);
      toast.success(r.data.already ? "You're already on the list \u2014 we'll reach out soon." : "You're in! We'll reach out with early access.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => !joined && setModalOpen(true)}
        disabled={joined}
        data-testid="program-interest-cta"
        className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[12px] font-semibold uppercase tracking-[0.14em] transition-all ${
          joined
            ? "cursor-default border border-emerald-400/40 bg-emerald-400/10 text-emerald-300"
            : "border border-sa-copper bg-sa-copper text-white hover:brightness-110 hover:-translate-y-0.5"
        }`}
      >
        {joined ? (
          <>
            <Check className="h-3.5 w-3.5" strokeWidth={2.8} /> Interest recorded
          </>
        ) : (
          <>
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2.4} />
            Reset My Liver
          </>
        )}
      </button>
      {count != null && (
        <span
          className="inline-flex items-center gap-2 text-[12px] font-medium text-white/70"
          data-testid="program-interest-count"
        >
          <span className="relative inline-flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
          </span>
          <span className="font-semibold text-white">{count.toLocaleString()}+</span>
          <span>already interested</span>
        </span>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" data-testid="program-interest-modal">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-sa-copper">
                  12-Week Recovery Program
                </p>
                <h3 className="mt-2 font-display text-2xl font-medium leading-tight text-sa-forest">
                  Reset your liver in 2027
                </h3>
              </div>
              <button onClick={() => setModalOpen(false)} className="rounded-full p-1 text-sa-muted hover:bg-sa-beige">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-3 text-sm text-sa-muted">
              We'll notify you first when the physician-led program opens for enrolment.
            </p>
            <div className="mt-5 space-y-3">
              <input
                type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Your name (optional)"
                data-testid="program-interest-name"
                className="w-full rounded-full border border-sa-beige bg-white px-5 py-3 text-sa-forest outline-none focus:border-sa-copper"
              />
              <input
                type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="Phone number"
                data-testid="program-interest-phone"
                className="w-full rounded-full border border-sa-beige bg-white px-5 py-3 text-sa-forest outline-none focus:border-sa-copper"
                autoFocus
              />
              <button
                onClick={submit} disabled={submitting}
                data-testid="program-interest-submit"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-sa-forest px-6 py-3.5 font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-60"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {submitting ? "Reserving..." : "Reset My Liver"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


const PILLARS = [
  {
    icon: HeartHandshake,
    title: "Lifestyle Medicine",
    body: "Nutrition, exercise, weight management, sleep optimization, alcohol reduction support.",
  },
  {
    icon: Pill,
    title: "Precision Therapeutic Interventions",
    body: "Personalized, evidence-based interventions including GLP-1 therapies, saroglitazar, lipid and glucose management, weight management, targeted nutraceuticals, micronutrient optimization, and treatment of metabolic comorbidities.",
  },
  {
    icon: ScanLine,
    title: "Advanced Imaging",
    body: "MRI-PDFF, MR Elastography, ultrasound and Doppler.",
  },
  {
    icon: Activity,
    title: "Advanced Monitoring",
    body: "Continuous tracking of liver stiffness, fat quantification, and metabolic health markers over time.",
  },
];

const PHASES = [
  {
    weeks: "Weeks 1–3",
    title: "Reset",
    body: "Baseline diagnostics, a liver-friendly nutrition reset, and removal of the biggest metabolic stressors.",
  },
  {
    weeks: "Weeks 4–6",
    title: "Rebuild",
    body: "Structured movement, insulin-sensitivity work, and targeted supplementation calibrated to your markers.",
  },
  {
    weeks: "Weeks 7–9",
    title: "Reduce",
    body: "Active reduction of liver fat and inflammation, with a mid-program re-scan to measure real change.",
  },
  {
    weeks: "Weeks 10–12",
    title: "Reinforce",
    body: "Lock in results, re-test key biomarkers, and transition to a personalized long-term sustain protocol.",
  },
];

export const Roadmap = () => {
  return (
    <section id="roadmap" className="bg-sa-forest py-12 md:py-16" data-testid="roadmap-section">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Reveal className="max-w-4xl">
          <div className="flex flex-wrap items-center gap-3">
            {/* Launching 2027 chip */}
            <span className="inline-flex items-center gap-2 rounded-full border border-sa-copper/40 bg-white/5 px-4 py-1.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-sa-copper">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sa-copper opacity-70" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-sa-copper" />
              </span>
              Launching 2027
            </span>
            <RecoveryProgramInterest />
          </div>
          <h2 className="mt-6 font-display text-4xl font-medium leading-[1.06] tracking-tight text-sa-copper sm:text-5xl lg:text-6xl">
            Signature 12-Week Precision Liver Recovery Program
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-white/60">
            A structured path from risk to restored. Recommended for Yellow, Orange,
            and Red risk patients following clinical evaluation. Four deliberate
            phases, measurable at every step.
          </p>
        </Reveal>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4"
        >
          {PHASES.map((p, i) => (
            <motion.div
              key={p.title}
              variants={fadeUp}
              className="group relative flex flex-col rounded-[28px] border border-white/10 bg-white/[0.04] p-7 transition-colors duration-300 hover:border-sa-copper/40"
            >
              <div className="mb-6 flex items-center justify-between">
                <span className="font-serif text-4xl italic text-sa-copper">
                  0{i + 1}
                </span>
                <span className="h-px w-10 bg-white/20 transition-all duration-300 group-hover:w-16 group-hover:bg-sa-copper" />
              </div>
              <p className="text-[12px] font-semibold uppercase tracking-widest text-white/40">
                {p.weeks}
              </p>
              <h3 className="mt-1 font-display text-2xl font-medium text-white">
                {p.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-white/60">{p.body}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Four pillars */}
        <div className="mt-20">
          <Reveal>
            <h3 className="font-display text-2xl font-medium tracking-tight text-white md:text-3xl">
              Built on four pillars
            </h3>
          </Reveal>
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
          >
            {PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  variants={fadeUp}
                  className="rounded-[28px] border border-white/10 bg-white/[0.04] p-7"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sa-copper/15 text-sa-copper">
                    <Icon className="h-5 w-5" />
                  </span>
                  <p className="mt-5 font-display text-lg font-medium text-white">{p.title}</p>
                  <p className="mt-2 text-sm leading-relaxed text-white/60">{p.body}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Roadmap;
